import { useState, useEffect, useRef, useCallback } from 'react';
import type { Editor } from '@tiptap/react';
import { useNavigate, useParams } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuArrowLeft, LuPin, LuPinOff, LuTrash2, LuCheck, LuUser,
    LuCalendar, LuTag, LuUsers, LuLoaderCircle,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import NoteEditor from '../components/NoteEditor';
import NoteToolbar from '../components/NoteToolbar';
import ConfirmationModal from '../components/ConfirmationModal';
import { AnotacaoService, ehDeOutroUsuario, type Anotacao } from '../services/AnotacaoService';
import './AnotacaoEditorPage.css';

/** Espera de digitação antes de gravar. Documento não tem botão Salvar. */
const ESPERA_AUTOSAVE = 1500;

type EstadoSalvamento = 'limpo' | 'pendente' | 'salvando' | 'salvo' | 'erro';

const AnotacaoEditorPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const ehNova = id === 'nova';

    const [anotacaoId, setAnotacaoId] = useState<number | null>(ehNova ? null : Number(id));
    const [carregando, setCarregando] = useState(!ehNova);
    const [titulo, setTitulo] = useState('');
    const [conteudo, setConteudo] = useState('');
    const [conteudoInicial, setConteudoInicial] = useState('');
    const [fixada, setFixada] = useState(false);
    const [etiquetas, setEtiquetas] = useState('');
    const [compartilhada, setCompartilhada] = useState(false);
    const [meta, setMeta] = useState<Pick<Anotacao, 'nomeUsuarioCriador' | 'dataCriacao'> | null>(null);
    const [somenteLeitura, setSomenteLeitura] = useState(false);
    /* A barra vive fora do NoteEditor (entre o cabeçalho e a folha), então a
       página precisa da instância do editor para poder comandá-lo. */
    const [editorAtivo, setEditorAtivo] = useState<Editor | null>(null);

    const [estado, setEstado] = useState<EstadoSalvamento>('limpo');
    const [horaSalvo, setHoraSalvo] = useState<string>('');
    const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

    const timerRef = useRef<number | null>(null);
    /* O autosave precisa do valor mais novo sem virar dependência do timer. */
    const dadosRef = useRef({ titulo, conteudo, fixada, etiquetas, compartilhada });
    useEffect(() => {
        dadosRef.current = { titulo, conteudo, fixada, etiquetas, compartilhada };
    }, [titulo, conteudo, fixada, etiquetas, compartilhada]);

    const idRef = useRef<number | null>(anotacaoId);
    useEffect(() => { idRef.current = anotacaoId; }, [anotacaoId]);

    useEffect(() => {
        if (ehNova) return;
        let ativo = true;
        (async () => {
            try {
                const r = await AnotacaoService.getById(Number(id));
                if (!ativo) return;
                if (!r.sucesso || !r.dados) {
                    aviso.erro(r.mensagem || 'Anotação não encontrada.');
                    navigate('/anotacoes');
                    return;
                }
                const a = r.dados;
                setTitulo(a.titulo ?? '');
                setConteudo(a.conteudo ?? '');
                setConteudoInicial(a.conteudo ?? '');
                setFixada(a.fixada);
                setEtiquetas(a.etiquetas ?? '');
                setCompartilhada(a.compartilhada);
                setMeta({ nomeUsuarioCriador: a.nomeUsuarioCriador, dataCriacao: a.dataCriacao });

                /* Alimenta a coluna "Última visita" da listagem. Vai solto, sem
                   await: é acessório e não pode atrasar a abertura da anotação. */
                void AnotacaoService.registrarVisita(a.anotacaoId);

                /* Compartilhar dá leitura, não escrita — a API recusa o PUT de quem
                   não é o autor, então a tela não pode fingir que dá para editar. */
                setSomenteLeitura(ehDeOutroUsuario(a));
            } catch {
                aviso.erro('Não foi possível carregar a anotação.');
                navigate('/anotacoes');
            } finally {
                if (ativo) setCarregando(false);
            }
        })();
        return () => { ativo = false; };
    }, [id, ehNova, navigate]);

    const gravar = useCallback(async () => {
        const dados = dadosRef.current;

        /* Não cria registro vazio só porque a pessoa abriu "Nova anotação"
           e desistiu — senão a lista enche de "Sem título". */
        const vazia = !dados.titulo.trim() && !dados.conteudo.replace(/<[^>]*>/g, '').trim();
        if (vazia && idRef.current === null) { setEstado('limpo'); return; }

        setEstado('salvando');
        const payload = {
            titulo: dados.titulo.trim() || null,
            conteudo: dados.conteudo,
            fixada: dados.fixada,
            etiquetas: dados.etiquetas.trim() || null,
            compartilhada: dados.compartilhada,
        };

        try {
            if (idRef.current === null) {
                const r = await AnotacaoService.create(payload);
                if (!r.sucesso) throw new Error(r.mensagem);
                setAnotacaoId(r.dados.anotacaoId);
                idRef.current = r.dados.anotacaoId;
                setMeta({ nomeUsuarioCriador: r.dados.nomeUsuarioCriador, dataCriacao: r.dados.dataCriacao });
                /* Troca a URL sem recarregar: a partir daqui F5 abre a anotação. */
                window.history.replaceState(null, '', `/anotacoes/${r.dados.anotacaoId}`);
            } else {
                const r = await AnotacaoService.update(idRef.current, payload);
                if (!r.sucesso) throw new Error(r.mensagem);
            }
            setEstado('salvo');
            setHoraSalvo(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
        } catch (e) {
            setEstado('erro');
            aviso.erro(textoDoErro(e, 'Não consegui salvar a anotação.'));
        }
    }, []);

    /* Agenda a gravação a cada mudança. */
    const agendar = useCallback(() => {
        if (somenteLeitura) return;
        setEstado('pendente');
        if (timerRef.current) window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => { void gravar(); }, ESPERA_AUTOSAVE);
    }, [gravar, somenteLeitura]);

    /* Sair da tela com gravação pendente perderia o último trecho digitado. */
    useEffect(() => () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            void gravar();
        }
    }, [gravar]);

    useEffect(() => {
        const antesDeSair = (e: BeforeUnloadEvent) => {
            if (estado === 'pendente' || estado === 'salvando') e.preventDefault();
        };
        window.addEventListener('beforeunload', antesDeSair);
        return () => window.removeEventListener('beforeunload', antesDeSair);
    }, [estado]);

    const excluir = async () => {
        if (anotacaoId === null) { navigate('/anotacoes'); return; }
        try {
            const r = await AnotacaoService.remove(anotacaoId);
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível excluir a anotação.'); return; }
            aviso.ok('Anotação excluída.');
            navigate('/anotacoes');
        } catch {
            aviso.erro('Não foi possível excluir a anotação.');
        }
    };

    const rotuloEstado = () => {
        if (somenteLeitura) return 'Somente leitura';
        switch (estado) {
            case 'salvando': return 'Salvando…';
            case 'salvo': return `Salvo ${horaSalvo}`;
            case 'pendente': return 'Alterações não salvas';
            case 'erro': return 'Não salvo';
            default: return anotacaoId ? 'Salvo' : 'Rascunho';
        }
    };

    if (carregando) {
        return (
            <div className="page-shell ae-screen">
                <PageHeader title="Anotação" />
                <div className="page-body"><p className="ae-carregando">Carregando…</p></div>
            </div>
        );
    }

    return (
        <div className="page-shell ae-screen">
            <PageHeader
                leading={
                    <button type="button" className="ae-voltar" onClick={() => navigate('/anotacoes')} title="Voltar para Anotações">
                        <LuArrowLeft size={16} />
                    </button>
                }
                title={titulo.trim() || 'Nova anotação'}
                subtitle={somenteLeitura ? `De ${meta?.nomeUsuarioCriador ?? 'outro usuário'} · somente leitura` : undefined}
                actions={
                    <div className="ae-actions">
                        <span className={`ae-estado is-${estado}`}>
                            {estado === 'salvando'
                                ? <LuLoaderCircle size={12} className="ae-girando" />
                                : <LuCheck size={12} />}
                            {rotuloEstado()}
                        </span>

                        {!somenteLeitura && (
                            <>
                                <button
                                    type="button"
                                    className={`ae-icone ${fixada ? 'is-on' : ''}`}
                                    onClick={() => { setFixada(f => !f); agendar(); }}
                                    title={fixada ? 'Desafixar' : 'Fixar no topo'}
                                    aria-pressed={fixada}
                                >
                                    {fixada ? <LuPin size={15} /> : <LuPinOff size={15} />}
                                </button>
                                <button
                                    type="button"
                                    className={`ae-icone ${compartilhada ? 'is-on' : ''}`}
                                    onClick={() => { setCompartilhada(c => !c); agendar(); }}
                                    title={compartilhada ? 'Deixar de compartilhar' : 'Compartilhar com a equipe'}
                                    aria-pressed={compartilhada}
                                >
                                    <LuUsers size={15} />
                                </button>
                            </>
                        )}
                    </div>
                }
            />

            {/* Barra de edição: fora do `.ae-body`, que é quem rola — assim ela
                continua à vista ao descer numa anotação longa. Some no modo
                leitura, onde não há o que formatar. */}
            {!somenteLeitura && editorAtivo && (
                <NoteToolbar editor={editorAtivo} anotacaoId={anotacaoId} />
            )}

            <div className="page-body ae-body">
                <div className="ae-col">
                    <input
                        className="ae-titulo"
                        value={titulo}
                        onChange={e => { setTitulo(e.target.value); agendar(); }}
                        placeholder="Sem título"
                        aria-label="Título da anotação"
                        readOnly={somenteLeitura}
                    />

                    <div className="ae-sub">
                        {meta?.nomeUsuarioCriador && (
                            <span><LuUser size={12} />{meta.nomeUsuarioCriador}</span>
                        )}
                        {meta?.dataCriacao && (
                            <span><LuCalendar size={12} />{new Date(meta.dataCriacao).toLocaleDateString('pt-BR')}</span>
                        )}
                        <label className="ae-etiquetas">
                            <LuTag size={12} />
                            <input
                                value={etiquetas}
                                onChange={e => { setEtiquetas(e.target.value); agendar(); }}
                                placeholder="etiquetas, separadas por vírgula"
                                aria-label="Etiquetas"
                                readOnly={somenteLeitura}
                            />
                        </label>
                    </div>

                    {somenteLeitura ? (
                        <div
                            className="ae-leitura"
                            /* Conteúdo de outro usuário da mesma empresa, gerado pelo
                               editor de blocos — não é entrada livre de terceiros. */
                            dangerouslySetInnerHTML={{ __html: conteudo }}
                        />
                    ) : (
                        <NoteEditor
                            conteudoInicial={conteudoInicial}
                            anotacaoId={anotacaoId}
                            onChange={html => { setConteudo(html); agendar(); }}
                            /* `setEditorAtivo` direto porque é estável: o `onReady`
                               do NoteEditor roda num efeito com o callback nas
                               dependências, e uma arrow inline recriaria o efeito
                               a cada render. */
                            onReady={setEditorAtivo}
                        />
                    )}
                </div>
            </div>

            {!somenteLeitura && (
                <footer className="ae-rodape">
                    <button type="button" className="ae-excluir" onClick={() => setConfirmandoExclusao(true)}>
                        <LuTrash2 size={14} />
                        <span>Excluir anotação</span>
                    </button>
                </footer>
            )}

            <ConfirmationModal
                isOpen={confirmandoExclusao}
                onClose={() => setConfirmandoExclusao(false)}
                onConfirm={() => { setConfirmandoExclusao(false); void excluir(); }}
                title="Excluir anotação"
                message={`Excluir “${titulo.trim() || 'Sem título'}”? Esta ação não pode ser desfeita.`}
                confirmText="Excluir"
                cancelText="Cancelar"
            />
        </div>
    );
};

export default AnotacaoEditorPage;
